import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { PRICING_PROFILE_DATE_RANGE_PATH } from '../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import { tzNormalizeDate } from '../../../../utils/date';
import {
  clearPricingProfileDateRangeFilter,
  getPricingProfileDateRangeFilter,
  postPricingProfileDateRange,
} from '../../../../actions/sales/PricingProfileDateRange';
import NewPricingProfileDateRangeForm from './NewPricingProfileDateRangeForm';

const ClonePricingProfileDateRange = ({
  breadcrumbs,
  corporate,
  dispatchClearPricingProfileDateRangeFilter,
  dispatchGetPricingProfileDateRangeFilter,
  dispatchPostPricingProfileDateRange,
  loading,
  pricingProfileDateRangeFilter,
  year,
}) => {
  useLayoutEffect(() => {
    if (year !== undefined && corporate !== undefined) {
      dispatchGetPricingProfileDateRangeFilter({ year, corporate });
    }

    return () => dispatchClearPricingProfileDateRangeFilter();
  }, [year, corporate]);

  const onSubmit = (formValues) => {
    const newFormValues = { ...formValues };

    newFormValues.pricingProfileDateRangeList = Array.isArray(
      formValues.pricingProfileDateRangeList,
    )
      ? formValues.pricingProfileDateRangeList.map(
          ({ startDate, endDate, reason }) => ({
            startDate: startDate ? tzNormalizeDate({ date: startDate }) : null,
            endDate: endDate ? tzNormalizeDate({ date: endDate }) : null,
            reason,
          }),
        )
      : [];

    dispatchPostPricingProfileDateRange(newFormValues);
  };

  const addOneYearSafe = (date) => {
    if (!date) return null;

    const d = new Date(date);
    const originalMonth = d.getMonth();

    d.setFullYear(d.getFullYear() + 1);

    if (d.getMonth() !== originalMonth) {
      d.setDate(0);
    }

    return d;
  };

  const generateInitialValues = () => ({
    year: year ? Number(year) + 1 : null,
    corporate: !!corporate,
    pricingProfileDateRangeList: pricingProfileDateRangeFilter
      .toJS()
      .map(({ startDate, endDate, reason }) => ({
        startDate: startDate
          ? addOneYearSafe(tzNormalizeDate({ date: startDate }))
          : null,
        endDate: endDate
          ? addOneYearSafe(tzNormalizeDate({ date: endDate }))
          : null,
        reason: reason || '',
      })),
  });

  let content;

  if (loading) {
    content = <Loader />;
  } else if (pricingProfileDateRangeFilter.isEmpty()) {
    content = <NoDataResource returnPage={PRICING_PROFILE_DATE_RANGE_PATH} />;
  } else {
    content = (
      <NewPricingProfileDateRangeForm
        onSubmit={onSubmit}
        initialValues={generateInitialValues()}
      />
    );
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Clonar Rango de Fechas"
      subtitle="Clonar rango de fechas para perfiles de precio"
      content={content}
    />
  );
};

ClonePricingProfileDateRange.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  corporate: PropTypes.bool,
  dispatchClearPricingProfileDateRangeFilter: PropTypes.func.isRequired,
  dispatchGetPricingProfileDateRangeFilter: PropTypes.func.isRequired,
  dispatchPostPricingProfileDateRange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  pricingProfileDateRangeFilter: PropTypes.instanceOf(Immutable.List)
    .isRequired,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ClonePricingProfileDateRange.defaultProps = {
  corporate: false,
  loading: false,
  year: null,
};

const mapDispatchToProps = {
  dispatchClearPricingProfileDateRangeFilter:
    clearPricingProfileDateRangeFilter,
  dispatchGetPricingProfileDateRangeFilter: getPricingProfileDateRangeFilter,
  dispatchPostPricingProfileDateRange: postPricingProfileDateRange,
};

const mapStateToProps = ({ SalesUnit }, ownProps) => {
  const routeState =
    ownProps.location && ownProps.location.state ? ownProps.location.state : {};

  return {
    year: routeState.year,
    corporate: routeState.corporate,
    breadcrumbs: [
      ...SalesUnit.UnitHome.get('breadcrumbs'),
      {
        text: 'Rango de Fechas para Perfiles de Precio',
        href: PRICING_PROFILE_DATE_RANGE_PATH,
      },
      {
        text: 'Clonar',
        href: '',
      },
    ],
    pricingProfileDateRangeFilter: SalesUnit.PricingProfileDateRange.getIn([
      'current',
      'filter',
    ]),
    loading: !SalesUnit.PricingProfileDateRange.getIn([
      'current',
      'activity',
    ]).isEmpty(),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ClonePricingProfileDateRange);
